import { useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { Alert, Badge, Button, Card, Col, Form, Row } from "react-bootstrap";
import { useQuery } from "@tanstack/react-query";
import { Link, Navigate, Outlet, useParams, useSearchParams } from "react-router-dom";
import { adminArtworkDetailSchema, adminCatalogPageSchema } from "@eaf/contracts";
import { ErrorState, LoadingState, PageFrame, ResponsiveTable } from "@eaf/ui";
import { useAuth } from "../auth/auth-context";
import {
  InspectionContent,
  MeasurementValue,
  StatusValue,
  TextValue,
  ValueList
} from "../components/admin-inspection-values";
import { Seo } from "../components/seo";

export function AdminGate() {
  const auth = useAuth();
  if (auth.state === "loading")
    return (
      <PageFrame>
        <LoadingState label="Verifying access" />
      </PageFrame>
    );
  if (auth.state === "anonymous") return <AdminLogin />;
  if (auth.state === "forbidden")
    return (
      <PageFrame narrow>
        <ErrorState title="EAF access is not assigned">{auth.message}</ErrorState>
      </PageFrame>
    );
  if (auth.state === "error")
    return (
      <PageFrame narrow>
        <ErrorState title="Authentication unavailable">{auth.message}</ErrorState>
      </PageFrame>
    );
  return <Outlet />;
}

function AdminLogin() {
  const auth = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    const data = new FormData(event.currentTarget);
    try {
      await auth.signIn(formText(data, "email"), formText(data, "password"));
    } catch {
      setError("Sign-in failed. Check the account and password, then try again.");
      setBusy(false);
    }
  };
  return (
    <PageFrame narrow>
      <Seo title="EAF Administration Sign In" description="Authorized EAF administration access." />
      <Card className="auth-card">
        <Card.Body>
          <p className="eyebrow">Administration</p>
          <h1>Sign in</h1>
          <p>Sign in with your EAF Authentication account.</p>
          {error ? <Alert variant="danger">{error}</Alert> : null}
          <Form onSubmit={submit}>
            <Form.Group className="mb-3" controlId="email">
              <Form.Label>Email</Form.Label>
              <Form.Control name="email" type="email" autoComplete="username" required />
            </Form.Group>
            <Form.Group className="mb-4" controlId="password">
              <Form.Label>Password</Form.Label>
              <Form.Control
                name="password"
                type="password"
                autoComplete="current-password"
                required
              />
            </Form.Group>
            <Button type="submit" disabled={busy}>
              {busy ? "Signing in…" : "Sign in"}
            </Button>
          </Form>
        </Card.Body>
      </Card>
    </PageFrame>
  );
}

export function AdminCatalogPage() {
  const auth = useAuth();
  const [params, setParams] = useSearchParams();
  const normalized = new URLSearchParams(params);
  if (!normalized.has("page")) normalized.set("page", "1");
  if (!normalized.has("pageSize")) normalized.set("pageSize", "25");
  if (!normalized.has("sort")) normalized.set("sort", "seq-asc");
  const query = useQuery({
    queryKey: ["admin-catalog", normalized.toString()],
    queryFn: () =>
      auth
        .authorizedFetch<unknown>(`/admin/catalog?${normalized}`)
        .then((data) => adminCatalogPageSchema.parse(data))
  });
  if (!auth.currentUser) return <Navigate to="/admin" replace />;
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const next = new URLSearchParams({
      page: "1",
      pageSize: "25",
      sort: formText(form, "sort") || "seq-asc"
    });
    const q = formText(form, "q");
    if (q) next.set("q", q);
    setParams(next);
  };
  return (
    <PageFrame>
      <Seo title="Artwork Administration | EAF" description="Read-only EAF artwork inspection." />
      <div className="admin-heading">
        <div>
          <p className="eyebrow">Read-only administration</p>
          <h1>Artwork register</h1>
          <p>
            Signed in as {auth.currentUser.displayName} · {auth.currentUser.roles.join(", ")}
          </p>
        </div>
        <Button variant="outline-light" onClick={() => void auth.logout()}>
          Sign out
        </Button>
      </div>
      <Alert variant="info">
        Inspection only. Editing, onboarding, uploads, publication changes, image processing, and AI
        actions are deliberately unavailable in this stage.
      </Alert>
      <Form key={params.toString()} className="catalog-controls" onSubmit={submit}>
        <Row className="g-3 align-items-end">
          <Col md={7}>
            <Form.Label htmlFor="admin-search">Search</Form.Label>
            <Form.Control id="admin-search" name="q" defaultValue={params.get("q") ?? ""} />
          </Col>
          <Col md={3}>
            <Form.Label htmlFor="admin-sort">Sort</Form.Label>
            <Form.Select id="admin-sort" name="sort" defaultValue={params.get("sort") ?? "seq-asc"}>
              <option value="seq-asc">Sequence</option>
              <option value="seq-desc">Sequence descending</option>
              <option value="title-asc">Title A–Z</option>
              <option value="edited-desc">Recently updated</option>
            </Form.Select>
          </Col>
          <Col md={2}>
            <Button type="submit" className="w-100">
              Apply
            </Button>
          </Col>
        </Row>
      </Form>
      {query.isLoading ? (
        <LoadingState label="Loading artwork register" />
      ) : query.isError || !query.data ? (
        <ErrorState retry={() => void query.refetch()}>
          The protected artwork register is unavailable.
        </ErrorState>
      ) : (
        <ResponsiveTable
          caption="Artwork register"
          headers={[
            "Image",
            "Title",
            "Seq",
            "Storage",
            "ID",
            "IDX",
            "Publish",
            "Created",
            "Inspect"
          ]}
          rows={query.data.items.map((item) => [
            <img className="admin-thumb" src={item.image.md} alt="" loading="lazy" />,
            item.title ?? "Untitled",
            item.seq ?? "—",
            item.storage ?? "—",
            item.id,
            item.idx ?? "—",
            <Badge bg={isTrue(item.publish) ? "success" : "secondary"}>
              {isTrue(item.publish) ? "Published" : "Not published"}
            </Badge>,
            formatDate(item.created),
            <Link
              to={`/admin/artwork/${encodeURIComponent(item.id)}?return=${encodeURIComponent(params.toString())}`}
            >
              Inspect
            </Link>
          ])}
        />
      )}
      {query.data ? (
        <nav className="pager" aria-label="Administration pages">
          <Button
            variant="outline-light"
            disabled={query.data.pagination.page <= 1}
            onClick={() => {
              const next = new URLSearchParams(params);
              next.set("page", String(query.data.pagination.page - 1));
              setParams(next);
            }}
          >
            Previous
          </Button>
          <span>
            Page {query.data.pagination.page} of {query.data.pagination.totalPages}
          </span>
          <Button
            variant="outline-light"
            disabled={query.data.pagination.page >= query.data.pagination.totalPages}
            onClick={() => {
              const next = new URLSearchParams(params);
              next.set("page", String(query.data.pagination.page + 1));
              setParams(next);
            }}
          >
            Next
          </Button>
        </nav>
      ) : null}
    </PageFrame>
  );
}

export function AdminArtworkPage() {
  const auth = useAuth();
  const { id = "" } = useParams();
  const [params] = useSearchParams();
  const query = useQuery({
    queryKey: ["admin-artwork", id],
    queryFn: () =>
      auth
        .authorizedFetch<unknown>(`/admin/catalog/${encodeURIComponent(id)}`)
        .then((data) => adminArtworkDetailSchema.parse(data)),
    enabled: Boolean(id)
  });
  const returnQuery = params.get("return");
  if (query.isLoading)
    return (
      <PageFrame>
        <LoadingState label="Loading artwork" />
      </PageFrame>
    );
  if (query.isError || !query.data)
    return (
      <PageFrame>
        <ErrorState title="Artwork unavailable">
          The protected artwork record could not be loaded.
        </ErrorState>
      </PageFrame>
    );
  const item = query.data;
  return (
    <PageFrame>
      <Link to={`/admin${returnQuery ? `?${returnQuery}` : ""}`}>← Back to register</Link>
      <div className="admin-heading mt-4">
        <div>
          <p className="eyebrow">Read-only inspection</p>
          <h1>{item.title || "Untitled"}</h1>
          <p>
            Registration {item.id} · Sequence {item.seq ?? "—"}
          </p>
        </div>
      </div>
      <Row className="g-4 align-items-start">
        <Col lg={4}>
          <img
            className="artwork-detail__image"
            src={item.image.lg}
            alt={item.title ?? "Artwork"}
          />
        </Col>
        <Col lg={8} className="inspection-layout">
          <InspectionSection title="Record identity and dates">
            <InspectionField label="ID" value={item.id} />
            <InspectionField label="IDX" value={item.idx} />
            <InspectionField label="Sequence" value={item.seq} />
            <InspectionField label="Artwork created" value={item.artCreated} />
            <InspectionField label="Record created" value={formatDate(item.created)} />
            <InspectionField label="Record edited" value={formatDate(item.edited)} />
          </InspectionSection>
          <InspectionSection title="Physical details">
            <InspectionField label="Paper" value={item.paper} />
            <InspectionField label="Press type" value={item.pressType} />
            <InspectionField label="Texture" value={item.texture} />
            <InspectionField label="Tint" value={item.tint} />
            <InspectionField label="Medium" value={<ValueList value={item.medium} />} />
            <InspectionField label="Orientation" value={item.orientation} />
            <InspectionField label="Ratio" value={item.ratio} />
            <InspectionField label="Width" value={<MeasurementValue value={item.width} />} />
            <InspectionField label="Height" value={<MeasurementValue value={item.height} />} />
          </InspectionSection>
          <InspectionSection title="Publication, sale, and storage">
            <InspectionField
              label="Publish"
              value={
                <StatusValue
                  value={item.publish}
                  trueLabel="Published"
                  falseLabel="Not published"
                />
              }
            />
            <InspectionField
              label="For sale"
              value={
                <StatusValue value={item.sale} trueLabel="For sale" falseLabel="Not for sale" />
              }
            />
            <InspectionField
              label="Sold"
              value={<StatusValue value={item.sold} trueLabel="Sold" falseLabel="Not sold" />}
            />
            <InspectionField label="Physical storage" value={item.storage} />
            <InspectionField label="Image storage" value={item.imageStorage} />
          </InspectionSection>
          <InspectionSection title="Condition">
            <InspectionField label="Condition" value={item.artCondition} />
            <InspectionField
              label="Condition details"
              value={<TextValue value={item.conditionDetails} />}
              wide
            />
          </InspectionSection>
        </Col>
      </Row>
      <div className="inspection-layout inspection-layout--full">
        <InspectionSection title="Writing">
          <InspectionField
            label="Excerpt / professional summary"
            value={<InspectionContent blocks={item.excerptBlocks} />}
            wide
          />
          <InspectionField
            label="Description"
            value={<InspectionContent blocks={item.descriptionBlocks} />}
            wide
          />
        </InspectionSection>
        <InspectionSection title="Classification and taxonomy">
          <InspectionField label="Primary category" value={item.categoryPrimary} />
          <InspectionField label="Secondary category" value={item.categorySecondary} />
          <InspectionField label="Genre" value={<ValueList value={item.genre} />} />
          <InspectionField
            label="Style"
            value={<InspectionContent blocks={item.styleBlocks} />}
            wide
          />
          <InspectionField label="Subject tags" value={<ValueList value={item.subjectTags} />} />
          <InspectionField label="Style traits" value={<ValueList value={item.styleTraits} />} />
          <InspectionField
            label="Environmental moods"
            value={<ValueList value={item.environmentalMoods} />}
          />
          <InspectionField
            label="Visual elements"
            value={<ValueList value={item.visualElements} />}
          />
          <InspectionField label="Movement" value={<ValueList value={item.movement} />} />
          <InspectionField label="Technique" value={<ValueList value={item.technique} />} />
          <InspectionField label="Influence" value={<ValueList value={item.influence} />} />
          <InspectionField
            label="Presentation context"
            value={<ValueList value={item.presentationContext} />}
          />
          <InspectionField
            label="Market positioning"
            value={<ValueList value={item.marketPositioning} />}
          />
        </InspectionSection>
      </div>
    </PageFrame>
  );
}

function InspectionSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="inspection-section">
      <h2>{title}</h2>
      <dl className="inspection-section__grid">{children}</dl>
    </section>
  );
}

function InspectionField({
  label,
  value,
  wide = false
}: {
  label: string;
  value: ReactNode;
  wide?: boolean;
}) {
  const empty = value === null || value === undefined || value === "";
  return (
    <div className={`inspection-field${wide ? " inspection-field--wide" : ""}`}>
      <dt>{label}</dt>
      <dd>{empty ? "—" : value}</dd>
    </div>
  );
}

function isTrue(value: string | null): boolean {
  return value?.toLowerCase() === "true";
}

function formatDate(value: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.valueOf()) ? value : date.toLocaleString();
}

function formText(form: FormData, key: string): string {
  const value = form.get(key);
  return typeof value === "string" ? value : "";
}
